/**
 * Admin - Call Center Team
 * Team stats: per-agent calls, conversions, overdue tasks.
 */

import { useState, useEffect } from "react";
import Card from "@mui/material/Card";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

import { callCenterAPI } from "utils/api";

function AdminCallCenter() {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState({ type: "", text: "" });

  useEffect(() => {
    callCenterAPI
      .getAnalyticsTeam()
      .then((res) => setAgents(res.agents || []))
      .catch((e) => {
        setMessage({ type: "error", text: e.message || "Failed to load team analytics" });
        setAgents([]);
      })
      .finally(() => setLoading(false));
  }, []);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <MDBox mb={2}>
          <MDTypography variant="h4" fontWeight="medium">
            Call Center Team
          </MDTypography>
          <MDTypography variant="body2" color="text.secondary">
            Per-agent stats: total calls, conversions this month, overdue tasks.
          </MDTypography>
        </MDBox>

        {message.text && (
          <Alert
            severity={message.type}
            onClose={() => setMessage({ type: "", text: "" })}
            sx={{ mb: 2 }}
          >
            {message.text}
          </Alert>
        )}

        <Card>
          <MDBox p={2}>
            {loading ? (
              <MDBox display="flex" justifyContent="center" py={4}>
                <CircularProgress />
              </MDBox>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Agent</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell align="right">Total calls</TableCell>
                      <TableCell align="right">Conversions (month)</TableCell>
                      <TableCell align="right">Overdue tasks</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {agents.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} align="center">
                          No call center agents or no data
                        </TableCell>
                      </TableRow>
                    ) : (
                      agents.map((a) => (
                        <TableRow key={a._id}>
                          <TableCell>{a.fullName || "—"}</TableCell>
                          <TableCell>{a.email || "—"}</TableCell>
                          <TableCell align="right">{a.callsCount ?? 0}</TableCell>
                          <TableCell align="right">{a.conversionsThisMonth ?? 0}</TableCell>
                          <TableCell align="right">{a.overdueTasks ?? 0}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </MDBox>
        </Card>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default AdminCallCenter;
